/* eslint-disable @typescript-eslint/no-explicit-any */
export const monthNames = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

const monthFullName = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// export const dateFormate = (dateval: string | number | Date) => {
//   const date = new Date(dateval);
//   return MomentUtil.formatWithLocale(date, "DD-MM-YYYY");
// };

import moment from "moment";

export const dateFormate = (dateval: string | number | Date) => {
  if (!dateval) return "-";

  const parsedDate = moment(
    dateval,
    ["D-MM-YYYY", "DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601],
    true,
  );

  if (!parsedDate.isValid()) return "-";

  return parsedDate.format("DD-MMM-YYYY").toUpperCase();
};

export const convert = (originalDateStr: any) => {
  const originalDate = new Date(originalDateStr);
  // Extract year, month, and day
  const year = originalDate.getFullYear();
  const month = (originalDate.getMonth() + 1).toString().padStart(2, "0"); // January is 0
  const day = originalDate.getDate().toString().padStart(2, "0");

  // Format the date as "yyyy-MM-dd"
  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

export const getYear = () => new Date().getFullYear();

export const getMonthByDate = (val: any) =>
  monthFullName[new Date(val).getMonth()];

// True when the given "YYYY-MM-DD" date falls in the current month or a future
// month. Parsed from the string parts so it stays timezone-safe. Used to decide
// whether a leave needs an upcoming-month balance check.
export const isCurrentOrFutureMonth = (dateStr?: string): boolean => {
  if (!dateStr) return false;
  const [year, month] = dateStr.split("-").map(Number);
  if (!year || !month) return false;
  const now = new Date();
  return year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1);
};

// function capitalize(word) {
//   return word.charAt(0).toUpperCase() + word.slice(1);
// }

export const capitalize = (word: string) => {
  if (word && word.length) {
    return word.charAt(0).toUpperCase() + word.slice(1).toLocaleLowerCase();
  }
  return "";
};

/**
 * Format date to "MMM DD, YYYY" format (e.g., "Aug 11, 2025")
 * @param dateStr - Date string or Date object
 * @returns Formatted date string in "MMM DD, YYYY" format or "N/A" if invalid
 */
export const formatDateToMonthDayYear = (dateStr?: string | Date): string => {
  if (!dateStr) return "N/A";

  const date = moment(dateStr);
  if (!date.isValid()) return "N/A";

  return date.format("MMM DD, YYYY");
};


/**
 * Compress image if it exceeds max size (default: 5MB)
 * Reduces quality + optionally resizes
 */
export const compressImage = (
  file: File,
  maxSizeMB = 5
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = (event: any) => {
      img.src = event.target.result;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      let width = img.width;
      let height = img.height;

      // Resize large images (prevents huge files)
      const MAX_WIDTH = 1920;
      if (width > MAX_WIDTH) {
        height = (height * MAX_WIDTH) / width;
        width = MAX_WIDTH;
      }

      canvas.width = width;
      canvas.height = height;

      ctx?.drawImage(img, 0, 0, width, height);

      let quality = 0.9;

      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject("Compression failed");

            const sizeMB = blob.size / (1024 * 1024);

            if (sizeMB <= maxSizeMB || quality <= 0.1) {
              resolve(new File([blob], file.name, { type: file.type }));
            } else {
              quality -= 0.1;
              tryCompress();
            }
          },
          file.type,
          quality
        );
      };

      tryCompress();
    };

    img.onerror = reject;
  });
};

/**
 * Converts a UTC date string, number, or Date object to IST (Asia/Kolkata) timezone.
 * Gracefully handles null, undefined, and invalid dates to prevent UI runtime errors.
 */
export const convertUtcToIst = (utcValue?: string | number | Date | null) => {
  const fallback = { date: "N/A", time: "N/A", full: "N/A" };

  if (!utcValue) return fallback;

  const date = new Date(utcValue);

  if (isNaN(date.getTime())) return fallback;

  try {
    const dateOptions: Intl.DateTimeFormatOptions = {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "short",
      day: "numeric",
    };

    const timeOptions: Intl.DateTimeFormatOptions = {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    const fullOptions: Intl.DateTimeFormatOptions = {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    };

    return {
      date: new Intl.DateTimeFormat("en-US", dateOptions).format(date),
      time: new Intl.DateTimeFormat("en-US", timeOptions).format(date),
      full: new Intl.DateTimeFormat("en-US", fullOptions).format(date),
    };
  } catch (error) {
    console.error("Error formatting date to IST:", error);
    return fallback;
  }
};

/**
 * Reusable utility to auto-capitalize the first letter of each word in an input field.
 * Restores the cursor position to prevent jumping during typing.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleNameInput = (
  e: React.FormEvent<HTMLInputElement>,
  setValue: (name: any, value: any, options?: any) => void
) => {
  const target = e.target as HTMLInputElement;
  const { name, value, selectionStart, selectionEnd } = target;

  if (!name) return;

  // Auto-capitalize the first letter of each word
  const capitalizedValue = value.replace(/(^\w{1})|(\s+\w{1})/g, (match) =>
    match.toUpperCase()
  );

  setValue(name, capitalizedValue, {
    shouldDirty: true,
    shouldValidate: true,
  });

  // Restore cursor position to prevent jumping while typing
  requestAnimationFrame(() => {
    target.setSelectionRange(selectionStart, selectionEnd);
  });
};

// util/preventSpecialCharInput.ts

export const allowOnlyAlphabets = (e: React.FormEvent<HTMLInputElement>) => {
  const input = e.currentTarget;
  // Strip out any character that is not a letter (a-z, A-Z) or a space
  const formattedValue = input.value.replace(/[^a-zA-Z\s]/g, "");

  if (input.value !== formattedValue) {
    // If invalid characters were typed, we remove them and shift the cursor back by 1
    const cursor = Math.max(0, (input.selectionStart ?? 1) - 1);
    input.value = formattedValue;

    // Restore cursor position seamlessly
    requestAnimationFrame(() => {
      input.setSelectionRange(cursor, cursor);
    });
  }
};